import React, { useState, useRef } from "react";
import apiService from "../../../services/api";
import { FILE_UPLOAD_CONFIG } from "../../../constants";

// ================== Types ==================
export interface Role {
  roleid: number;
  rolename: string;
}

export interface UserData {
  [key: string]: any;
}

export interface UseCommonDataReturn {
  roles: Role[];
  rolesLoading: boolean;
  fetchRoles: () => Promise<void>;

  uploadingImages: { [key: string]: boolean };
  setUploadingImages: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  handleImageUpload: (
    field: string,
    file: File,
    onSuccess: (field: string, imageUrl: string) => void,
    onError: (error: string) => void,
    folderPath?: string,
    folderType?: string
  ) => Promise<void>;

  fetchUserData: (userId: string) => Promise<UserData | null>;
}

// ================== Shared Upload Logic ==================
const validateFile = (file: File, onError: (error: string) => void): boolean => {
  if (!FILE_UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.includes(file.type)) {
    onError("Please select a valid image file (JPEG, PNG, GIF, WebP)");
    return false;
  }

  return true;
};

// ================== Common Hook ==================
export const useCommonData = (): UseCommonDataReturn => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<{ [key: string]: boolean }>({});
  const hasFetchedRoles = useRef(false);

  // Fetch roles
  const fetchRoles = async (): Promise<void> => {
    if (hasFetchedRoles.current) return;
    hasFetchedRoles.current = true;

    setRolesLoading(true);
    try {
      const response = (await apiService.getAllRoles()) as any;
      setRoles(response.data || response || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw new Error("Failed to load roles. Please refresh the page.");
    } finally {
      setRolesLoading(false);
    }
  };

  // Generic Image Upload
  const handleImageUpload = async (
    field: string,
    file: File,
    onSuccess: (field: string, imageUrl: string) => void,
    onError: (error: string) => void,
    folderPath: string = FILE_UPLOAD_CONFIG.FOLDER_PATHS.STAFF,
    folderType?: string
  ): Promise<void> => {
    if (!validateFile(file, onError)) return;

    setUploadingImages((prev) => ({ ...prev, [field]: true }));
    try {
      // Decide folder type dynamically if not passed
      const finalFolderType =
        folderType ||
        (field === "frontImage" || field === "backImage"
          ? FILE_UPLOAD_CONFIG.FOLDER_TYPES.ID_PROOF
          : FILE_UPLOAD_CONFIG.FOLDER_TYPES.PROFILE);

      const response = await apiService.uploadImage(file, folderPath, finalFolderType);

      const imageUrl =
        response?.data?.url ||
        response?.data?.filePath ||
        response?.data?.fileName ||
        response?.data?.imageUrl ||
        file.name;

      onSuccess(field, imageUrl);
    } catch (error) {
      console.error("Error uploading image:", error);
      onError(`Failed to upload ${field}. Please try again.`);
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Fetch user data
  const fetchUserData = async (userId: string): Promise<UserData | null> => {
    try {
      const response = await apiService.getUserById(userId);
      return (response as any)?.data || response;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw new Error("Failed to load user data. Please try again.");
    }
  };

  return {
    roles,
    rolesLoading,
    fetchRoles,
    uploadingImages,
    setUploadingImages,
    handleImageUpload,
    fetchUserData,
  };
};

// ================== Hub Hook ==================
export const useHubData = () => {
  const { handleImageUpload, ...commonData } = useCommonData();

  // Hub-specific image upload
  const handleHubImageUpload = (
    field: string,
    file: File,
    onSuccess: (field: string, imageUrl: string) => void,
    onError: (error: string) => void
  ) => {
    return handleImageUpload(
      field,
      file,
      onSuccess,
      onError,
      FILE_UPLOAD_CONFIG.FOLDER_PATHS.HUBS,
      FILE_UPLOAD_CONFIG.FOLDER_TYPES.DOCUMENT
    );
  };

  return {
    ...commonData,
    handleHubImageUpload,
  };
};

export default useCommonData;
